import React from 'react';
import { Box, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { TranslationKeys } from 'app/translations';
import { useBulkLeftCompanyPage } from './useBulkLeftCompanyPage';
import { UploadStep } from './components/UploadStep';
import { ReviewStep } from './components/ReviewStep';
import { SuccessStep } from './components/SuccessStep';

// ─── Page ─────────────────────────────────────────────────────────────────────

const STEPS = [
  TranslationKeys.adminPanel_bulkLeftCompany_step_upload,
  TranslationKeys.adminPanel_bulkLeftCompany_step_review,
  TranslationKeys.adminPanel_bulkLeftCompany_step_confirm,
] as const;

export const BulkLeftCompanyPage = () => {
  const intl = useIntl();
  const {
    activeStep,
    csvFile,
    parseErrors,
    employees,
    backupData,
    backupError,
    isConfirmOpen,
    setIsConfirmOpen,
    isSubmitting,
    isLoadingBackup,
    canLoadPreview,
    handleDownloadTemplate,
    handleFileUpload,
    handleLoadPreview,
    handleOpenConfirm,
    handleConfirm,
    handleReset,
  } = useBulkLeftCompanyPage();

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom fontWeight={700}>
        {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_title })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {intl.formatMessage({ id: TranslationKeys.adminPanel_bulkLeftCompany_description })}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map(stepKey => (
          <Step key={stepKey}>
            <StepLabel>{intl.formatMessage({ id: stepKey })}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <UploadStep
          csvFile={csvFile}
          employees={employees}
          parseErrors={parseErrors}
          backupError={backupError}
          canLoadPreview={canLoadPreview}
          isLoadingBackup={isLoadingBackup}
          onFileUpload={handleFileUpload}
          onLoadPreview={handleLoadPreview}
          onDownloadTemplate={handleDownloadTemplate}
        />
      )}

      {activeStep === 1 && backupData && (
        <ReviewStep
          backupData={backupData}
          employees={employees}
          isSubmitting={isSubmitting}
          isConfirmOpen={isConfirmOpen}
          setIsConfirmOpen={setIsConfirmOpen}
          onOpenConfirm={handleOpenConfirm}
          onConfirm={handleConfirm}
          onReset={handleReset}
        />
      )}

      {activeStep === 2 && <SuccessStep onReset={handleReset} />}
    </Box>
  );
};
